import React, { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import Image from '@atlaskit/image';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import {
	onAIAnswerCopyButtonClicked,
	onAIAnswerTabClick,
	onNonGenerativeAIAnswerResultShown,
} from '../../../ai-answer-dialog/analytics';
import { SainAction, useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { AIAnswerTab, aiAnswerTabsOrder, AnswerCardType } from '../../../constants';
import { AIDisclaimerAndFeedback } from '../../common/ui/ai-disclaimer-and-feedback';
import { AITabs } from '../../common/ui/ai-tabs';
import { AITabsHeader } from '../../common/ui/ai-tabs-header';
import { AITabsPanel } from '../../common/ui/ai-tabs-panel';
import { AITabsSources } from '../../common/ui/ai-tabs-sources';
import LocationDark from '../assets/location-dark.svg';
import LocationLight from '../assets/location-light.svg';
import { StandardActionsKnowledgeCard } from '../standard-cards/StandardActionsKnowledgeCard';

const messages = defineMessages({
	knowledge_card_location_category: {
		id: 'search_ai_knowledge_card_location_category',
		defaultMessage: 'Location',
		description: 'Category header for location knowledge card',
	},
	knowledge_card_location_google_maps_action: {
		id: 'search_ai_knowledge_card_location_google_maps_action',
		defaultMessage: 'Show on Google Maps',
		description: 'Button to show in Google',
	},
});

const styles = cssMap({
	fullHeight: {
		height: '100%',
	},
	containerStyles: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingLeft: token('space.0'),
		paddingBottom: token('space.0'),
	},
	disclaimer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
});

const generateGoogleMapsLink = (location: string) => {
	const encodedAddress = encodeURIComponent(location);
	return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
};

export const LocationCard = ({ location }: { location: string }): React.JSX.Element => {
	const [selectedTab, setSelectedTab] = useState<AIAnswerTab>(AIAnswerTab.AI_ANSWER);

	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const { brand, commonAttributes } = useAIAnswerContext();
	const { answerString, query, queryHash, searchSessionId, sources } = commonAttributes;

	const handleTabChange = useCallback(
		(selectedTabIndex: number) => {
			const selectedTab = aiAnswerTabsOrder[selectedTabIndex];
			setSelectedTab(selectedTab);

			fireAnalyticsEvent(
				onAIAnswerTabClick({
					...commonAttributes,
					selectedTab,
				}),
			);
		},
		[setSelectedTab, fireAnalyticsEvent, commonAttributes],
	);

	const copyLocation = async (location: string) => {
		fireSAINUsageEvent({ metCriteria: SainAction.COPY_CLICKED, commonAttributes });
		fireAnalyticsEvent(onAIAnswerCopyButtonClicked(commonAttributes));
		await navigator.clipboard.writeText(location);
	};

	const onClickGoogleMaps = useCallback(() => {
		fireAnalyticsEvent(
			onNonGenerativeAIAnswerResultShown({
				answerString,
				brand,
				query,
				queryHash,
				searchSessionId,
				source: 'location_knowledge_card',
				answerCardType: AnswerCardType.LOCATION,
			}),
		);
		fireSAINUsageEvent({ metCriteria: SainAction.SHOW_ON_GOOGLE_MAPS_CLICKED, commonAttributes });
	}, [
		answerString,
		brand,
		fireAnalyticsEvent,
		fireSAINUsageEvent,
		query,
		queryHash,
		searchSessionId,
		commonAttributes,
	]);

	const googleMapsLink = generateGoogleMapsLink(location);

	return (
		<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
			<AITabs onChange={handleTabChange} selectedTabIndex={aiAnswerTabsOrder.indexOf(selectedTab)}>
				<AITabsHeader showSourcesTab={sources.length > 0} />
				<AITabsPanel>
					<StandardActionsKnowledgeCard
						name="smart-answer-location-knowledge-card"
						category={formatMessage(messages.knowledge_card_location_category)}
						primaryDescription={location}
						actions={[
							<LinkButton href={googleMapsLink} onClick={onClickGoogleMaps}>
								{formatMessage(messages.knowledge_card_location_google_maps_action)}
							</LinkButton>,
							<IconButton icon={CopyIcon} label="copy" onClick={() => copyLocation(location)} />,
						]}
						media={<Image src={LocationLight} srcDark={LocationDark} alt={''} />}
						size="full"
						containerStyles={styles.containerStyles}
					/>
				</AITabsPanel>
				{sources.length > 0 ? (
					<AITabsPanel>
						<AITabsSources />
					</AITabsPanel>
				) : null}
			</AITabs>
			<Box xcss={styles.disclaimer}>
				<AIDisclaimerAndFeedback showFeedbackButtons={true} showCopyButton copyText={location} />
			</Box>
		</Stack>
	);
};
